import React, { useState } from 'react';
import {
  Type,
  Square,
  Image,
  MousePointer2,
  Minus,
  PenTool,
  Layers,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Palette,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Italic,
  ChevronDown,
  ChevronRight,
  Zap,
  Group,
  Trash2,
  Spline,
} from 'lucide-react';
import { useStudioStore } from '../../stores/studioStore';
import { useCanvasStore } from '../../stores/canvasStore';
import { useMappingStore } from '../../stores/mappingStore';
import {
  isFontLoaded,
  loadCustomFont,
  loadVariant,
  getFontVariantById,
  getFontsByFamily,
} from '../../lib/fontLoader';
import { defaultTextShadow } from '../../lib/elementDefaults';
import type {
  CanvasElement,
  TextElement,
  ShapeElement,
  ImageElement,
  ButtonElement,
  LineElement,
  PathElement,
  SvgElement,
  GroupElement,
} from '../../types';
import { recalcPathElement } from '../../lib/pathUtils';
import { expandSubtree } from '../../lib/groupUtils';
import DynamicFieldPanel from './mapping/DynamicFieldPanel';
import FontPicker, { type FontVariantSelection } from './FontPicker';
import AppearanceSection from './AppearanceSection';
import TextPathControls from './TextPathControls';
import ToggleSwitch from './ToggleSwitch';
import NoSelectionPanel from './NoSelectionPanel';
import MultiSelectColorEditor from './MultiSelectColorEditor';

type SectionKey = 'transform' | 'appearance' | 'lineProps' | 'pathProps' | 'typography' | 'textPath' | 'layer' | 'content' | 'dynamic' | 'accessibility';

export default function PropertyPanel() {
  const {
    canvasState,
    selectedElementId,
    selectedElementIds,
    updateElement,
    updateElements,
    removeElement,
    groupSelected,
    ungroupSelected,
    clearSelection,
    setCanvasState,
  } = useStudioStore();
  const { canvasWidth, canvasHeight } = useCanvasStore();
  const setCanvasDimensions = useStudioStore((s) => s.setCanvasDimensions);
  const { elementMappings } = useMappingStore();

  const [expandedSections, setExpandedSections] = useState<Set<SectionKey>>(
    new Set(['transform', 'appearance', 'lineProps', 'pathProps', 'typography', 'layer', 'content'])
  );

  const toggleSection = (key: SectionKey) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedElement = selectedElementId
    ? canvasState.elements.find((el) => el.id === selectedElementId)
    : null;

  const update = (updates: Partial<CanvasElement>) => {
    if (selectedElementId) {
      updateElement(selectedElementId, updates);
    }
  };

  if (selectedElementIds.length > 1) {
    return (
      <div className="w-80 flex-shrink-0 bg-[#111] border-l border-white/[0.06] flex flex-col h-full overflow-hidden">
        <div className="px-4 py-3 border-b border-white/[0.06]">
          <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">
            {selectedElementIds.length} Elements Selected
          </h3>
          <p className="text-[10px] text-zinc-600 mt-1 leading-relaxed">
            Use the alignment toolbar to align or distribute.
          </p>
        </div>
        <div className="p-4 space-y-3">
          <div className="space-y-1.5">
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">
              Opacity (all)
            </label>
            <input
              type="range"
              min={0}
              max={100}
              defaultValue={100}
              onChange={(e) => {
                const opacity = parseInt(e.target.value) / 100;
                updateElements(selectedElementIds.map((id) => ({ id, changes: { opacity } })));
              }}
              className="w-full"
            />
          </div>
          <MultiSelectColorEditor
            elements={canvasState.elements.filter((el) => selectedElementIds.includes(el.id))}
            onUpdateElements={updateElements}
          />

          <div className="space-y-1.5">
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">
              Dynamic Mapping
            </label>
            <DynamicFieldPanel
              elementId={selectedElementIds[0]}
              elementType={
                (canvasState.elements.find((el) => el.id === selectedElementIds[0])?.type ?? 'shape') as
                  'text' | 'image' | 'shape' | 'button' | 'path' | 'svg' | 'line'
              }
              contentType="DYNAMIC"
              elementIds={selectedElementIds}
              elementTypes={
                canvasState.elements
                  .filter((el) => selectedElementIds.includes(el.id))
                  .map((el) => el.type) as Array<'text' | 'image' | 'shape' | 'button' | 'path' | 'svg' | 'line' | 'group'>
              }
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">
              Quick Actions
            </label>
            <button
              onClick={() => { console.log('[GROUP-ENTRY-PROPERTYPANEL]', { stage: 'property-panel-group-clicked', timestamp: Date.now() }); groupSelected(); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-400 bg-white/[0.03] border border-white/[0.06] rounded-lg hover:bg-white/[0.06] hover:text-zinc-200 transition-colors"
            >
              <Group size={13} />
              Group Selected
              <span className="ml-auto text-[10px] text-zinc-600">Ctrl+G</span>
            </button>
            <button
              onClick={() => {
                for (const id of selectedElementIds) {
                  removeElement(id);
                }
                clearSelection();
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-400/60 bg-red-500/[0.03] border border-red-500/[0.06] rounded-lg hover:bg-red-500/[0.06] hover:text-red-400 transition-colors"
            >
              <Trash2 size={13} />
              Delete Selected
              <span className="ml-auto text-[10px] text-red-400/30">Del</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedElement) {
    const artboardSettings = (
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
            Dimensions
          </label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-zinc-600 mb-1 block">Width</label>
              <input
                type="number"
                value={canvasWidth}
                onChange={(e) => {
                  const w = parseInt(e.target.value) || 1200;
                  setCanvasDimensions(w, canvasHeight);
                }}
                className="w-full px-2 py-1.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-zinc-200 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
            <div>
              <label className="text-[10px] text-zinc-600 mb-1 block">Height</label>
              <input
                type="number"
                value={canvasHeight}
                onChange={(e) => {
                  const h = parseInt(e.target.value) || 628;
                  setCanvasDimensions(canvasWidth, h);
                }}
                className="w-full px-2 py-1.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-zinc-200 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
            Presets
          </label>
          <div className="space-y-1">
            {[
              { label: 'Hero Standard (1140x410)', w: 1140, h: 410 },
              { label: 'Hero Wide (1600x500)', w: 1600, h: 500 },
              { label: 'Email Header (600x200)', w: 600, h: 200 },
              { label: 'Social Square (1080x1080)', w: 1080, h: 1080 },
              { label: 'Facebook (1200x630)', w: 1200, h: 630 },
              { label: 'Instagram Story (1080x1920)', w: 1080, h: 1920 },
              { label: 'Leaderboard (728x90)', w: 728, h: 90 },
              { label: 'Half Page (300x600)', w: 300, h: 600 },
              { label: 'Medium Rect (300x250)', w: 300, h: 250 },
            ].map((preset) => (
              <button
                key={preset.label}
                onClick={() => {
                  setCanvasDimensions(preset.w, preset.h);
                }}
                className={`w-full text-left px-2 py-1.5 rounded-md text-xs transition-colors ${
                  canvasWidth === preset.w && canvasHeight === preset.h
                    ? 'bg-cyan-500/10 text-cyan-400'
                    : 'text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-300'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );

    return (
      <div className="w-80 flex-shrink-0 bg-[#111] border-l border-white/[0.06] flex flex-col h-full overflow-hidden">
        <div className="px-4 py-3 border-b border-white/[0.06]">
          <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">
            Toolbox
          </h3>
        </div>
        <NoSelectionPanel artboardSettingsContent={artboardSettings} />
      </div>
    );
  }

  const isText = selectedElement.type === 'text';
  const isShape = selectedElement.type === 'shape';
  const isImage = selectedElement.type === 'image';
  const isButton = selectedElement.type === 'button';
  const isGroup = selectedElement.type === 'group';
  const isLine = selectedElement.type === 'line';
  const isPath = selectedElement.type === 'path';
  const isSvg = selectedElement.type === 'svg';

  const GROUP_MAPPABLE_CHILD_TYPES = new Set(['text', 'shape', 'button', 'line']);
  const groupMappableChildren = isGroup
    ? expandSubtree(canvasState.elements, [selectedElement.id]).filter(
        (el) => el.id !== selectedElement.id && GROUP_MAPPABLE_CHILD_TYPES.has(el.type)
      )
    : [];

  const typeIcon = isText ? Type : isShape ? Square : isImage ? Image : isLine ? Minus : isPath ? PenTool : isSvg ? Spline : MousePointer2;
  const TypeIcon = typeIcon;

  return (
    <div className="w-80 flex-shrink-0 bg-[#111] border-l border-white/[0.06] flex flex-col h-full overflow-hidden">
      <div className="px-4 py-3 border-b border-white/[0.06] flex items-center gap-2">
        <TypeIcon size={14} className="text-cyan-400" />
        <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider flex-1 truncate">
          {selectedElement.name}
        </h3>
        <button
          onClick={() => update({ visible: !selectedElement.visible })}
          className="p-1 rounded hover:bg-white/[0.06] text-zinc-500 hover:text-zinc-300 transition-colors"
          title={selectedElement.visible ? 'Hide element' : 'Show element'}
        >
          {selectedElement.visible ? <Eye size={13} /> : <EyeOff size={13} />}
        </button>
        <button
          onClick={() => update({ locked: !selectedElement.locked })}
          className="p-1 rounded hover:bg-white/[0.06] text-zinc-500 hover:text-zinc-300 transition-colors"
          title={selectedElement.locked ? 'Unlock element' : 'Lock element'}
        >
          {selectedElement.locked ? <Lock size={13} /> : <Unlock size={13} />}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto sidebar-scroll">
        <div className="px-4 py-3 border-b border-white/[0.04]">
          <label className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5 block flex items-center gap-2">
            Name
            {(selectedElement.type === 'text' || selectedElement.type === 'button') && (
              <span className="text-[9px] font-medium text-zinc-600 normal-case tracking-normal">
                auto from text
              </span>
            )}
          </label>
          <input
            type="text"
            value={selectedElement.name}
            onChange={(e) => update({ name: e.target.value })}
            readOnly={selectedElement.type === 'text' || selectedElement.type === 'button'}
            title={
              selectedElement.type === 'text' || selectedElement.type === 'button'
                ? 'Text layer names mirror the text content and cannot be edited directly.'
                : undefined
            }
            className={`w-full px-2 py-1.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-zinc-200 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 ${
              selectedElement.type === 'text' || selectedElement.type === 'button'
                ? 'cursor-not-allowed text-zinc-400'
                : ''
            }`}
          />
        </div>

        <PropertySection
          title="Transform"
          sectionKey="transform"
          isOpen={expandedSections.has('transform')}
          onToggle={() => toggleSection('transform')}
        >
          <div className="grid grid-cols-2 gap-2">
            <NumberField label="X" value={selectedElement.x} onChange={(v) => update({ x: v })} />
            <NumberField label="Y" value={selectedElement.y} onChange={(v) => update({ y: v })} />
            <NumberField label="W" value={selectedElement.width} onChange={(v) => update({ width: v })} min={1} />
            <NumberField label="H" value={selectedElement.height} onChange={(v) => update({ height: v })} min={1} />
            <NumberField label="Rotation" value={selectedElement.rotation} onChange={(v) => update({ rotation: v })} suffix="deg" />
          </div>
        </PropertySection>

        {!isGroup && (
          <PropertySection
            title="Appearance"
            sectionKey="appearance"
            icon={<Palette size={13} className="text-amber-400" />}
            isOpen={expandedSections.has('appearance')}
            onToggle={() => toggleSection('appearance')}
          >
            <AppearanceSection element={selectedElement} onUpdate={update} onUpdateElement={updateElement} />
          </PropertySection>
        )}

        {isGroup && (() => {
          const groupEl = selectedElement as GroupElement;
          const childElements = canvasState.elements.filter((el) => groupEl.childIds.includes(el.id));
          return (
            <div className="border-b border-white/[0.04]">
              <div className="px-4 py-2.5 flex items-center gap-2">
                <Group size={13} className="text-cyan-400" />
                <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">
                  Group Contents
                </span>
                <span className="text-[10px] text-white/25 ml-auto">
                  {childElements.length} item{childElements.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="px-4 pb-3">
                <button
                  onClick={() => { console.log('[UNGROUP-ENTRY-PROPERTYPANEL]', { stage: 'property-panel-ungroup-clicked', timestamp: Date.now() }); ungroupSelected(); }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-400 bg-white/[0.03] border border-white/[0.06] rounded-lg hover:bg-white/[0.06] hover:text-zinc-200 transition-colors"
                >
                  <Group size={13} />
                  Ungroup
                  <span className="ml-auto text-[10px] text-zinc-600">Ctrl+Shift+G</span>
                </button>
              </div>
              <MultiSelectColorEditor
                elements={childElements}
                onUpdateElements={updateElements}
              />
            </div>
          );
        })()}

        {isSvg && (
          <div className="px-4 py-3 border-b border-white/[0.04] space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Aspect Ratio Lock</span>
              <ToggleSwitch
                checked={(selectedElement as SvgElement).preserveAspectRatio}
                onChange={(v) => update({ preserveAspectRatio: v } as Partial<CanvasElement>)}
              />
            </div>
            {(selectedElement as SvgElement).sourceFileName && (
              <div>
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold block mb-1">Source File</span>
                <span className="text-[11px] text-zinc-400 truncate block">{(selectedElement as SvgElement).sourceFileName}</span>
              </div>
            )}
          </div>
        )}

        {isLine && (
          <PropertySection
            title="Line Properties"
            sectionKey="lineProps"
            icon={<Minus size={13} className="text-cyan-400" />}
            isOpen={expandedSections.has('lineProps')}
            onToggle={() => toggleSection('lineProps')}
          >
            <LinePropertiesControls element={selectedElement as LineElement} onUpdate={update} />
          </PropertySection>
        )}

        {isPath && (
          <PropertySection
            title="Path Properties"
            sectionKey="pathProps"
            icon={<PenTool size={13} className="text-cyan-400" />}
            isOpen={expandedSections.has('pathProps')}
            onToggle={() => toggleSection('pathProps')}
          >
            <PathPropertiesControls element={selectedElement as PathElement} />
          </PropertySection>
        )}

        {(isText || isButton) && (
          <PropertySection
            title="Typography"
            sectionKey="typography"
            icon={<Type size={13} className="text-blue-400" />}
            isOpen={expandedSections.has('typography')}
            onToggle={() => toggleSection('typography')}
          >
            <TypographyControls element={selectedElement} onUpdate={update} isText={isText} />
          </PropertySection>
        )}

        {isText && (
          <PropertySection
            title="Text Path"
            sectionKey="textPath"
            icon={<Spline size={13} className="text-teal-400" />}
            isOpen={expandedSections.has('textPath')}
            onToggle={() => toggleSection('textPath')}
          >
            <TextPathControls
              element={selectedElement as TextElement}
              elements={canvasState.elements}
              onUpdate={(updates) => update(updates as Partial<CanvasElement>)}
            />
          </PropertySection>
        )}

        <PropertySection
          title="Layer Assignment"
          sectionKey="layer"
          icon={<Layers size={13} className="text-cyan-400" />}
          isOpen={expandedSections.has('layer')}
          onToggle={() => toggleSection('layer')}
        >
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">
                Export Layer
              </label>
              <div className="grid grid-cols-2 gap-1">
                <button
                  onClick={() => update({ layer: 'BACKGROUND_LAYER' })}
                  className={`px-2 py-1.5 rounded-md text-[10px] font-medium transition-colors ${
                    selectedElement.layer === 'BACKGROUND_LAYER'
                      ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                      : 'bg-white/[0.03] text-zinc-500 border border-white/[0.06] hover:bg-white/[0.06]'
                  }`}
                >
                  Background
                </button>
                <button
                  onClick={() => update({ layer: 'OVERLAY_LAYER' })}
                  className={`px-2 py-1.5 rounded-md text-[10px] font-medium transition-colors ${
                    selectedElement.layer === 'OVERLAY_LAYER'
                      ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                      : 'bg-white/[0.03] text-zinc-500 border border-white/[0.06] hover:bg-white/[0.06]'
                  }`}
                >
                  Overlay
                </button>
              </div>
              <p className="text-[9px] text-zinc-600 leading-relaxed">
                {selectedElement.layer === 'BACKGROUND_LAYER'
                  ? 'Exported as part of the background JPEG image. No screen reader access.'
                  : 'Exported as HTML/CSS overlay. Screen-reader accessible for ADA compliance.'}
              </p>
            </div>

            {!isGroup && !isPath && (
              <div className="space-y-1.5">
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">
                  Content Type
                </label>
                <div className="grid grid-cols-2 gap-1">
                  <button
                    onClick={() => update({ contentType: 'STATIC' })}
                    className={`px-2 py-1.5 rounded-md text-[10px] font-medium transition-colors flex items-center justify-center gap-1 ${
                      selectedElement.contentType === 'STATIC'
                        ? 'bg-zinc-500/15 text-zinc-300 border border-zinc-500/30'
                        : 'bg-white/[0.03] text-zinc-500 border border-white/[0.06] hover:bg-white/[0.06]'
                    }`}
                  >
                    <Lock size={10} />
                    Static
                  </button>
                  <button
                    onClick={() => update({ contentType: 'DYNAMIC' })}
                    className={`px-2 py-1.5 rounded-md text-[10px] font-medium transition-colors flex items-center justify-center gap-1 ${
                      selectedElement.contentType === 'DYNAMIC'
                        ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                        : 'bg-white/[0.03] text-zinc-500 border border-white/[0.06] hover:bg-white/[0.06]'
                    }`}
                  >
                    <Zap size={10} />
                    Dynamic
                  </button>
                </div>
                <p className="text-[9px] text-zinc-600 leading-relaxed">
                  {selectedElement.contentType === 'STATIC'
                    ? 'Fixed content -- same across all schools.'
                    : 'Mapped to brand data -- changes per school.'}
                </p>
              </div>
            )}
          </div>
        </PropertySection>

        {isImage && (
          <PropertySection
            title="Image Source"
            sectionKey="content"
            icon={<Image size={13} className="text-emerald-400" />}
            isOpen={expandedSections.has('content')}
            onToggle={() => toggleSection('content')}
          >
            <ImageSourceControls element={selectedElement as ImageElement} onUpdate={update} />
          </PropertySection>
        )}

        {isButton && (
          <PropertySection
            title="Button Content"
            sectionKey="content"
            icon={<MousePointer2 size={13} className="text-cyan-400" />}
            isOpen={expandedSections.has('content')}
            onToggle={() => toggleSection('content')}
          >
            <ButtonContentControls element={selectedElement as ButtonElement} onUpdate={update} />
          </PropertySection>
        )}

        {(isImage || isSvg) && (
          <PropertySection
            title="Accessibility"
            sectionKey="accessibility"
            isOpen={expandedSections.has('accessibility')}
            onToggle={() => toggleSection('accessibility')}
          >
            <div className="space-y-2">
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">
                Alt Text (Screen Reader)
              </label>
              <textarea
                value={(selectedElement as ImageElement | SvgElement).alt || ''}
                onChange={(e) => update({ alt: e.target.value })}
                placeholder="Describe this image for screen readers (leave blank if decorative)"
                className="w-full px-3 py-2 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-zinc-200 resize-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                rows={3}
                maxLength={250}
              />
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-zinc-600">
                  {(selectedElement as ImageElement | SvgElement).alt
                    ? `${(selectedElement as ImageElement | SvgElement).alt!.length}/250`
                    : 'Decorative (aria-hidden)'}
                </span>
                {(selectedElement as ImageElement | SvgElement).alt &&
                  (selectedElement as ImageElement | SvgElement).alt!.length > 200 && (
                  <span className="text-amber-500">Getting long</span>
                )}
              </div>
            </div>
          </PropertySection>
        )}

        {!isGroup && (
          <PropertySection
            title="Dynamic Field Mapping"
            sectionKey="dynamic"
            icon={<Zap size={13} className="text-purple-400" />}
            isOpen={expandedSections.has('dynamic')}
            onToggle={() => toggleSection('dynamic')}
            badge={
              (elementMappings[selectedElement.id] ?? []).length > 0 ? (
                <span className="ml-auto mr-2 text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-400 border border-purple-500/20">
                  {(elementMappings[selectedElement.id] ?? []).length}
                </span>
              ) : null
            }
          >
            <DynamicFieldPanel
              elementId={selectedElement.id}
              elementType={selectedElement.type as 'text' | 'shape' | 'image' | 'button' | 'path' | 'svg'}
              contentType={selectedElement.contentType}
            />
          </PropertySection>
        )}
        {isGroup && groupMappableChildren.length > 0 && (
          <PropertySection
            title="Dynamic Field Mapping"
            sectionKey="dynamic"
            icon={<Zap size={13} className="text-purple-400" />}
            isOpen={expandedSections.has('dynamic')}
            onToggle={() => toggleSection('dynamic')}
            badge={
              <span className="ml-auto mr-2 text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-400 border border-purple-500/20">
                {groupMappableChildren.length} children
              </span>
            }
          >
            <div className="text-[11px] text-zinc-500 mb-2 px-1">
              Mappings apply to all {groupMappableChildren.length} mappable elements inside this group.
            </div>
            <DynamicFieldPanel
              elementId={groupMappableChildren[0].id}
              elementType={groupMappableChildren[0].type as 'text' | 'image' | 'shape' | 'button' | 'path' | 'svg' | 'line'}
              contentType="DYNAMIC"
              elementIds={groupMappableChildren.map((el) => el.id)}
              elementTypes={groupMappableChildren.map((el) => el.type) as Array<'text' | 'image' | 'shape' | 'button' | 'path' | 'svg' | 'line' | 'group'>}
            />
          </PropertySection>
        )}
        {isGroup && groupMappableChildren.length === 0 && (
          <PropertySection
            title="Dynamic Field Mapping"
            sectionKey="dynamic"
            icon={<Zap size={13} className="text-purple-400" />}
            isOpen={expandedSections.has('dynamic')}
            onToggle={() => toggleSection('dynamic')}
          >
            <div className="text-[11px] text-zinc-500 px-1 py-2">
              This group contains no mappable elements (text, shape, button, or line).
            </div>
          </PropertySection>
        )}
      </div>
    </div>
  );
}

function TypographyControls({
  element,
  onUpdate,
  isText,
}: {
  element: CanvasElement;
  onUpdate: (updates: Partial<CanvasElement>) => void;
  isText: boolean;
}) {
  const textEl = element as TextElement;

  const handleVariantChange = async (sel: FontVariantSelection) => {
    const variant = getFontVariantById(sel.variantId);
    if (variant) {
      // Ensure the chosen variant's @font-face has actually downloaded/parsed
      // before the store change kicks the Fabric sync. If we skip this, the
      // textbox re-measures against the fallback font and the width snap
      // produces either dead space on the right or clipped glyphs.
      if (!isFontLoaded(variant.fontFamily)) {
        await loadCustomFont(variant.fontFamily);
      }
      await loadVariant(variant);
    }
    // widthIntent resets to 'auto' so the sync layer snaps the box to the
    // freshly-measured content for the new variant. User can grab a handle
    // to flip back to 'manual'.
    onUpdate({
      fontFamily: sel.fontFamily,
      fontWeight: sel.fontWeight,
      fontStyle: sel.fontStyle,
      fontVariantId: sel.variantId,
      widthIntent: 'auto',
    } as Partial<CanvasElement>);
  };

  // Surface an italic toggle only when the current variant has a matching
  // italic sibling at the same weight (or vice versa). Forcing italic on a
  // family that lacks an italic variant is what forced browser-synthesized
  // obliques and broke advance-width measurement.
  const siblings = getFontsByFamily(textEl.fontFamily);
  const targetWeight = textEl.fontWeight || 400;
  const italicSibling = siblings.find(
    (f) => (parseInt(f.fontWeight, 10) || 400) === targetWeight && f.fontStyle === 'italic'
  );
  const normalSibling = siblings.find(
    (f) => (parseInt(f.fontWeight, 10) || 400) === targetWeight && f.fontStyle !== 'italic'
  );
  const canToggleItalic = Boolean(italicSibling && normalSibling);

  const handleItalicToggle = async () => {
    const goingItalic = textEl.fontStyle !== 'italic';
    const target = goingItalic ? italicSibling : normalSibling;
    if (!target) return;
    await handleVariantChange({
      variantId: target.id,
      fontFamily: target.fontFamily,
      fontWeight: parseInt(target.fontWeight, 10) || 400,
      fontStyle: target.fontStyle === 'italic' ? 'italic' : 'normal',
    });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
          Font
        </label>
        <FontPicker
          variantId={textEl.fontVariantId}
          family={textEl.fontFamily}
          onSelectVariant={handleVariantChange}
        />
      </div>

      <div>
        <NumberField
          label="Size"
          value={textEl.fontSize}
          onChange={(v) => onUpdate({ fontSize: v } as Partial<CanvasElement>)}
          min={8}
          max={200}
          suffix="px"
        />
      </div>

      {isText && (
        <div className="flex items-center gap-1">
          {canToggleItalic && (
            <ToggleButton
              active={textEl.fontStyle === 'italic'}
              onClick={handleItalicToggle}
              title="Italic"
            >
              <Italic size={13} />
            </ToggleButton>
          )}
          <div className="w-px h-5 bg-white/[0.06] mx-1" />
          <ToggleButton
            active={textEl.textAlign === 'left'}
            onClick={() => onUpdate({ textAlign: 'left' } as Partial<CanvasElement>)}
            title="Align Left"
          >
            <AlignLeft size={13} />
          </ToggleButton>
          <ToggleButton
            active={textEl.textAlign === 'center'}
            onClick={() => onUpdate({ textAlign: 'center' } as Partial<CanvasElement>)}
            title="Align Center"
          >
            <AlignCenter size={13} />
          </ToggleButton>
          <ToggleButton
            active={textEl.textAlign === 'right'}
            onClick={() => onUpdate({ textAlign: 'right' } as Partial<CanvasElement>)}
            title="Align Right"
          >
            <AlignRight size={13} />
          </ToggleButton>
        </div>
      )}

      {isText && (
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label="Line Height"
            value={textEl.lineHeight}
            onChange={(v) => onUpdate({ lineHeight: v } as Partial<CanvasElement>)}
            min={0.5}
            max={4}
            step={0.1}
          />
          <NumberField
            label="Spacing"
            value={textEl.letterSpacing}
            onChange={(v) => onUpdate({ letterSpacing: v } as Partial<CanvasElement>)}
            min={-5}
            max={50}
            suffix="px"
          />
        </div>
      )}

      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
          Color
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={textEl.color}
            onChange={(e) => onUpdate({ color: e.target.value } as Partial<CanvasElement>)}
            className="w-8 h-8 rounded-md border border-white/10 cursor-pointer bg-transparent"
          />
          <input
            type="text"
            value={textEl.color}
            onChange={(e) => onUpdate({ color: e.target.value } as Partial<CanvasElement>)}
            className="flex-1 px-2 py-1.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-zinc-200 font-mono focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
          />
        </div>
      </div>

      {isText && (
        <>
          <div className="space-y-1.5">
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold block">
              Decoration
            </label>
            <div className="flex gap-1">
              {([
                { value: 'none' as const, label: 'None', display: 'Aa' },
                { value: 'underline' as const, label: 'Underline', display: 'U' },
                { value: 'line-through' as const, label: 'Strikethrough', display: 'S' },
              ]).map(({ value, label, display }) => (
                <button
                  key={value}
                  onClick={() => onUpdate({ textDecoration: value } as Partial<CanvasElement>)}
                  title={label}
                  className={`flex-1 px-2 py-1.5 text-xs rounded-md border transition-colors ${
                    (textEl.textDecoration ?? 'none') === value
                      ? 'bg-cyan-500/15 border-cyan-500/30 text-cyan-400'
                      : 'bg-[#0a0a0a] border-white/[0.06] text-zinc-500 hover:border-white/[0.1]'
                  }`}
                >
                  {display}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold block">
              Transform
            </label>
            <div className="flex gap-1">
              {([
                { value: 'none' as const, label: 'None', display: 'Aa' },
                { value: 'uppercase' as const, label: 'Uppercase', display: 'AA' },
                { value: 'lowercase' as const, label: 'Lowercase', display: 'aa' },
                { value: 'capitalize' as const, label: 'Capitalize', display: 'Ab' },
              ]).map(({ value, label, display }) => (
                <button
                  key={value}
                  onClick={() => onUpdate({ textTransform: value, widthIntent: 'auto' } as Partial<CanvasElement>)}
                  title={label}
                  className={`flex-1 px-2 py-1.5 text-xs rounded-md border transition-colors ${
                    (textEl.textTransform ?? 'none') === value
                      ? 'bg-cyan-500/15 border-cyan-500/30 text-cyan-400'
                      : 'bg-[#0a0a0a] border-white/[0.06] text-zinc-500 hover:border-white/[0.1]'
                  }`}
                >
                  {display}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold block">
              Text Containment
            </label>
            <div className="flex gap-1">
              {([
                { value: 'natural' as const, label: 'Natural — box grows with text', display: 'Natural' },
                { value: 'contain-within-textbox' as const, label: 'Contain within textbox — shrink to fit fixed dimensions', display: 'CWT' },
                { value: 'contain-within-structure' as const, label: 'Contain within structure — delimiter-based lines (Build Prompt D)', display: 'CWS' },
              ]).map(({ value, label, display }) => {
                const current = textEl.textFitMode ?? 'natural';
                const handle = () => {
                  const patch: Partial<TextElement> = { textFitMode: value };
                  if (value === 'contain-within-textbox') {
                    const lines = (textEl.text || '').split('\n').length;
                    patch.committedLineCount = lines;
                    patch.widthIntent = 'manual';
                  } else if (value === 'contain-within-structure') {
                    patch.widthIntent = 'manual';
                  }
                  // 'natural' intentionally does not touch widthIntent — flipping
                  // back to 'auto' would snap the user's manual box width away
                  // via the sync-on-measure layer. committedLineCount is left
                  // in place (harmless when mode !== 'contain-within-textbox').
                  onUpdate(patch as Partial<CanvasElement>);
                };
                return (
                  <button
                    key={value}
                    onClick={handle}
                    title={label}
                    className={`flex-1 px-2 py-1.5 text-[11px] rounded-md border transition-colors ${
                      current === value
                        ? 'bg-cyan-500/15 border-cyan-500/30 text-cyan-400'
                        : 'bg-[#0a0a0a] border-white/[0.06] text-zinc-500 hover:border-white/[0.1]'
                    }`}
                  >
                    {display}
                  </button>
                );
              })}
            </div>
          </div>

          <TextShadowControl element={textEl} onUpdate={onUpdate} />

          <div className="space-y-1.5">
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold block">
              Text Outline
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={textEl.strokeColor || '#000000'}
                onChange={(e) => onUpdate({ strokeColor: e.target.value } as Partial<CanvasElement>)}
                className="w-7 h-7 rounded border border-white/10 cursor-pointer bg-transparent"
              />
              <input
                type="number"
                min={0}
                max={10}
                step={0.5}
                value={textEl.textStrokeWidth ?? 0}
                onChange={(e) => onUpdate({ textStrokeWidth: parseFloat(e.target.value) || 0 } as Partial<CanvasElement>)}
                placeholder="0"
                className="flex-1 px-2 py-1.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-zinc-200 text-center focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
              />
              <span className="text-[9px] text-zinc-600">px</span>
            </div>
          </div>
        </>
      )}

      {isText && (
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
            Text Content
          </label>
          <textarea
            value={textEl.text}
            onChange={(e) => onUpdate({ text: e.target.value } as Partial<CanvasElement>)}
            rows={3}
            className="w-full px-2 py-1.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-zinc-200 resize-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
            placeholder="Enter text..."
          />
        </div>
      )}
    </div>
  );
}

function TextShadowControl({
  element,
  onUpdate,
}: {
  element: TextElement;
  onUpdate: (updates: Partial<CanvasElement>) => void;
}) {
  const ts = element.textShadow || defaultTextShadow();

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">
          Text Shadow
        </label>
        <ToggleSwitch
          checked={ts.enabled}
          onChange={(checked) => onUpdate({ textShadow: { ...ts, enabled: checked } } as Partial<CanvasElement>)}
        />
      </div>

      {ts.enabled && (
        <div className="space-y-2 pl-2 border-l border-white/[0.06]">
          <div className="flex items-center gap-2">
            <label className="text-[10px] text-zinc-500 w-10 shrink-0">Color</label>
            <input
              type="color"
              value={(ts.color || '#000000').slice(0, 7)}
              onChange={(e) => onUpdate({ textShadow: { ...ts, color: e.target.value + '88' } } as Partial<CanvasElement>)}
              className="w-6 h-6 rounded border border-white/[0.06] cursor-pointer bg-transparent"
            />
          </div>

          {([
            { key: 'offsetX' as const, label: 'X', min: -20, max: 20 },
            { key: 'offsetY' as const, label: 'Y', min: -20, max: 20 },
            { key: 'blur' as const, label: 'Blur', min: 0, max: 20 },
          ]).map(({ key, label, min, max }) => (
            <div key={key} className="flex items-center gap-2">
              <label className="text-[10px] text-zinc-500 w-10 shrink-0">{label}</label>
              <input
                type="range"
                min={min}
                max={max}
                value={ts[key]}
                onChange={(e) =>
                  onUpdate({ textShadow: { ...ts, [key]: parseInt(e.target.value) } } as Partial<CanvasElement>)
                }
                className="flex-1"
              />
              <span className="text-[10px] text-zinc-400 tabular-nums font-mono w-6 text-right">
                {ts[key]}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ImageSourceControls({
  element,
  onUpdate,
}: {
  element: ImageElement;
  onUpdate: (updates: Partial<CanvasElement>) => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
          Image URL
        </label>
        <input
          type="text"
          value={element.src}
          onChange={(e) => onUpdate({ src: e.target.value } as Partial<CanvasElement>)}
          className="w-full px-2 py-1.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-zinc-200 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
          placeholder="https://..."
        />
      </div>
      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
          Alt Text
        </label>
        <input
          type="text"
          value={element.alt}
          onChange={(e) => onUpdate({ alt: e.target.value } as Partial<CanvasElement>)}
          className="w-full px-2 py-1.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-zinc-200 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
          placeholder="Describe this image..."
        />
      </div>
      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
          Fit Mode
        </label>
        <select
          value={element.objectFit}
          onChange={(e) => onUpdate({ objectFit: e.target.value } as Partial<CanvasElement>)}
          className="w-full px-2 py-1.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-zinc-200 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
        >
          <option value="cover">Cover</option>
          <option value="contain">Contain</option>
          <option value="fill">Fill</option>
          <option value="none">None</option>
        </select>
      </div>
    </div>
  );
}

function ButtonContentControls({
  element,
  onUpdate,
}: {
  element: ButtonElement;
  onUpdate: (updates: Partial<CanvasElement>) => void;
}) {
  const maxRadius = Math.min((element.width || 180) / 2, (element.height || 48) / 2);
  const getResolvedElementValue = useStudioStore((s) => s.getResolvedElementValue);

  const displayText = getResolvedElementValue(element, 'textContent')
    ?? getResolvedElementValue(element, 'buttonLabel')
    ?? element.text;
  const displayColor = getResolvedElementValue(element, 'fontColor') ?? element.color ?? '#ffffff';
  const displayBackground = getResolvedElementValue(element, 'buttonFillColor') ?? element.backgroundColor ?? '#06b6d4';
  const isTransparent = element.backgroundColor === 'transparent';

  return (
    <div className="space-y-3">
      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
          Label
        </label>
        <input
          type="text"
          value={displayText}
          onChange={(e) => onUpdate({ text: e.target.value } as Partial<CanvasElement>)}
          className="w-full px-2 py-1.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-zinc-200 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
        />
      </div>

      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
          Corner Radius
        </label>
        <div className="flex items-center gap-2">
          <input
            type="range"
            min={0}
            max={maxRadius}
            value={element.borderRadius ?? 8}
            onChange={(e) => onUpdate({ borderRadius: Number(e.target.value) } as Partial<CanvasElement>)}
            className="flex-1 h-1 bg-white/[0.06] rounded-full appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2.5 [&::-webkit-slider-thumb]:h-2.5
              [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-cyan-500"
          />
          <input
            type="number"
            min={0}
            max={maxRadius}
            value={element.borderRadius ?? 8}
            onChange={(e) => onUpdate({ borderRadius: Number(e.target.value) } as Partial<CanvasElement>)}
            className="w-12 px-1.5 py-0.5 bg-[#0a0a0a] border border-white/[0.06] rounded text-[10px] font-mono text-white text-center focus:outline-none focus:border-cyan-500/50"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
            Text Color
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={displayColor}
              onChange={(e) => onUpdate({ color: e.target.value } as Partial<CanvasElement>)}
              className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent"
            />
            <span className="text-[10px] text-zinc-500 font-mono">{displayColor}</span>
          </div>
        </div>
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
            Background
          </label>
          {isTransparent ? (
            <div className="text-[10px] text-zinc-600 font-mono py-1">transparent</div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={displayBackground}
                onChange={(e) => onUpdate({ backgroundColor: e.target.value } as Partial<CanvasElement>)}
                className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent"
              />
              <span className="text-[10px] text-zinc-500 font-mono">{displayBackground}</span>
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">
            Transparent Fill
          </label>
          <ToggleSwitch
            checked={isTransparent}
            onChange={(checked) =>
              onUpdate({ backgroundColor: checked ? 'transparent' : '#06b6d4' } as Partial<CanvasElement>)
            }
          />
        </div>
        {isTransparent && (
          <p className="text-[10px] text-zinc-600 mt-1 leading-relaxed">
            Button is invisible — renders as an ADA click overlay in exports.
          </p>
        )}
      </div>

      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
          Link URL
        </label>
        <input
          type="text"
          value={element.href || ''}
          onChange={(e) => onUpdate({ href: e.target.value } as Partial<CanvasElement>)}
          className="w-full px-2 py-1.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-zinc-200 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
          placeholder="https://..."
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <NumberField
          label="Padding X"
          value={element.paddingX}
          onChange={(v) => onUpdate({ paddingX: v } as Partial<CanvasElement>)}
          min={0}
          max={80}
          suffix="px"
        />
        <NumberField
          label="Padding Y"
          value={element.paddingY}
          onChange={(v) => onUpdate({ paddingY: v } as Partial<CanvasElement>)}
          min={0}
          max={80}
          suffix="px"
        />
      </div>
    </div>
  );
}

function LinePropertiesControls({
  element,
  onUpdate,
}: {
  element: LineElement;
  onUpdate: (updates: Partial<CanvasElement>) => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
          Stroke Color
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={element.stroke}
            onChange={(e) => onUpdate({ stroke: e.target.value } as Partial<CanvasElement>)}
            className="w-8 h-8 rounded-md border border-white/10 cursor-pointer bg-transparent"
          />
          <input
            type="text"
            value={element.stroke}
            onChange={(e) => onUpdate({ stroke: e.target.value } as Partial<CanvasElement>)}
            className="flex-1 px-2 py-1.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-zinc-200 font-mono focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
          />
        </div>
      </div>

      <NumberField
        label="Stroke Width"
        value={element.strokeWidth}
        onChange={(v) => onUpdate({ strokeWidth: v } as Partial<CanvasElement>)}
        min={1}
        max={50}
        suffix="px"
      />

      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
          Dash Style
        </label>
        <div className="grid grid-cols-4 gap-1">
          {(['solid', 'dashed', 'dotted', 'dash-dot'] as const).map((style) => (
            <button
              key={style}
              onClick={() => onUpdate({ strokeDashStyle: style } as Partial<CanvasElement>)}
              className={`px-2 py-1.5 rounded-md text-[10px] font-medium transition-colors capitalize ${
                element.strokeDashStyle === style
                  ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                  : 'bg-white/[0.03] text-zinc-500 border border-white/[0.06] hover:bg-white/[0.06]'
              }`}
            >
              {style === 'dash-dot' ? 'D-D' : style}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
          Line Cap
        </label>
        <div className="grid grid-cols-3 gap-1">
          {(['butt', 'round', 'square'] as const).map((cap) => (
            <button
              key={cap}
              onClick={() => onUpdate({ lineCap: cap } as Partial<CanvasElement>)}
              className={`px-2 py-1.5 rounded-md text-[10px] font-medium transition-colors capitalize ${
                element.lineCap === cap
                  ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                  : 'bg-white/[0.03] text-zinc-500 border border-white/[0.06] hover:bg-white/[0.06]'
              }`}
            >
              {cap}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
          Arrow Start
        </label>
        <div className="grid grid-cols-3 gap-1">
          {(['none', 'triangle', 'circle'] as const).map((style) => (
            <button
              key={style}
              onClick={() => onUpdate({ arrowStart: style } as Partial<CanvasElement>)}
              className={`px-2 py-1.5 rounded-md text-[10px] font-medium transition-colors capitalize ${
                element.arrowStart === style
                  ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                  : 'bg-white/[0.03] text-zinc-500 border border-white/[0.06] hover:bg-white/[0.06]'
              }`}
            >
              {style}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
          Arrow End
        </label>
        <div className="grid grid-cols-3 gap-1">
          {(['none', 'triangle', 'circle'] as const).map((style) => (
            <button
              key={style}
              onClick={() => onUpdate({ arrowEnd: style } as Partial<CanvasElement>)}
              className={`px-2 py-1.5 rounded-md text-[10px] font-medium transition-colors capitalize ${
                element.arrowEnd === style
                  ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                  : 'bg-white/[0.03] text-zinc-500 border border-white/[0.06] hover:bg-white/[0.06]'
              }`}
            >
              {style}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function PathPropertiesControls({ element }: { element: PathElement }) {
  const { updateElement } = useStudioStore();
  const id = element.id;

  const toggleClosed = (newClosed: boolean) => {
    const result = recalcPathElement(element.anchors, element.x, element.y, newClosed);
    updateElement(id, { closed: newClosed, pathData: result.pathData } as Partial<CanvasElement>);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Path Type</label>
        <div className="flex gap-1">
          <button
            onClick={() => toggleClosed(false)}
            className={`px-2 py-1 text-[10px] rounded transition-colors ${
              !element.closed
                ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                : 'bg-white/[0.03] text-zinc-500 border border-white/[0.06] hover:bg-white/[0.06]'
            }`}
          >
            Open
          </button>
          <button
            onClick={() => toggleClosed(true)}
            className={`px-2 py-1 text-[10px] rounded transition-colors ${
              element.closed
                ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                : 'bg-white/[0.03] text-zinc-500 border border-white/[0.06] hover:bg-white/[0.06]'
            }`}
          >
            Closed
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Anchor Points</label>
        <span className="text-[10px] text-zinc-400 bg-white/[0.04] px-2 py-0.5 rounded">{element.anchors?.length ?? 0}</span>
      </div>

      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">Stroke Color</label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={element.stroke || '#ffffff'}
            onChange={(e) => updateElement(id, { stroke: e.target.value } as Partial<CanvasElement>)}
            className="w-8 h-8 rounded-md border border-white/10 cursor-pointer bg-transparent"
          />
          <input
            type="text"
            value={element.stroke || '#ffffff'}
            onChange={(e) => updateElement(id, { stroke: e.target.value } as Partial<CanvasElement>)}
            className="flex-1 px-2 py-1.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-zinc-200 font-mono focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
          />
        </div>
      </div>

      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">Stroke Width</label>
        <div className="flex items-center gap-2">
          <input
            type="range"
            min={0}
            max={20}
            step={0.5}
            value={element.strokeWidth ?? 2}
            onChange={(e) => updateElement(id, { strokeWidth: parseFloat(e.target.value) } as Partial<CanvasElement>)}
            className="flex-1"
          />
          <span className="text-[10px] text-zinc-400 w-8 text-right tabular-nums">{element.strokeWidth ?? 2}</span>
        </div>
      </div>

      {element.closed && (
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">Fill Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={element.fill !== 'transparent' ? element.fill : '#000000'}
              onChange={(e) => updateElement(id, { fill: e.target.value, fillOpacity: 1 } as Partial<CanvasElement>)}
              className="w-8 h-8 rounded-md border border-white/10 cursor-pointer bg-transparent"
            />
            <button
              onClick={() => updateElement(id, { fill: 'transparent', fillOpacity: 0 } as Partial<CanvasElement>)}
              className="text-[10px] text-zinc-500 hover:text-zinc-300 whitespace-nowrap"
            >
              None
            </button>
          </div>
        </div>
      )}

      {element.closed && element.fill && element.fill !== 'transparent' && (
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">Fill Opacity</label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={element.fillOpacity ?? 1}
              onChange={(e) => updateElement(id, { fillOpacity: parseFloat(e.target.value) } as Partial<CanvasElement>)}
              className="flex-1"
            />
            <span className="text-[10px] text-zinc-400 w-10 text-right tabular-nums">{Math.round((element.fillOpacity ?? 1) * 100)}%</span>
          </div>
        </div>
      )}

      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">Stroke Style</label>
        <div className="grid grid-cols-4 gap-1">
          {(['solid', 'dashed', 'dotted', 'dash-dot'] as const).map((style) => (
            <button
              key={style}
              onClick={() => updateElement(id, { strokeDashStyle: style } as Partial<CanvasElement>)}
              className={`px-2 py-1.5 rounded-md text-[10px] font-medium transition-colors capitalize ${
                (element.strokeDashStyle || 'solid') === style
                  ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                  : 'bg-white/[0.03] text-zinc-500 border border-white/[0.06] hover:bg-white/[0.06]'
              }`}
            >
              {style === 'dash-dot' ? 'D-D' : style}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">Line Cap</label>
        <div className="grid grid-cols-3 gap-1">
          {(['butt', 'round', 'square'] as const).map((cap) => (
            <button
              key={cap}
              onClick={() => updateElement(id, { lineCap: cap } as Partial<CanvasElement>)}
              className={`px-2 py-1.5 rounded-md text-[10px] font-medium transition-colors capitalize ${
                (element.lineCap || 'round') === cap
                  ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                  : 'bg-white/[0.03] text-zinc-500 border border-white/[0.06] hover:bg-white/[0.06]'
              }`}
            >
              {cap}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">Line Join</label>
        <div className="grid grid-cols-3 gap-1">
          {(['miter', 'round', 'bevel'] as const).map((join) => (
            <button
              key={join}
              onClick={() => updateElement(id, { lineJoin: join } as Partial<CanvasElement>)}
              className={`px-2 py-1.5 rounded-md text-[10px] font-medium transition-colors capitalize ${
                (element.lineJoin || 'miter') === join
                  ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                  : 'bg-white/[0.03] text-zinc-500 border border-white/[0.06] hover:bg-white/[0.06]'
              }`}
            >
              {join}
            </button>
          ))}
        </div>
      </div>

      <div className="pt-2 border-t border-white/[0.04]">
        <p className="text-[9px] text-zinc-600 italic">Double-click the path on canvas to edit anchor points</p>
      </div>
    </div>
  );
}

function PropertySection({
  title,
  sectionKey: _sectionKey,
  icon,
  isOpen,
  onToggle,
  badge,
  children,
}: {
  title: string;
  sectionKey: string;
  icon?: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-white/[0.04]">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 w-full px-4 py-2.5 text-left hover:bg-white/[0.02] transition-colors"
      >
        {icon}
        <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider flex-1">
          {title}
        </span>
        {badge}
        {isOpen ? (
          <ChevronDown size={12} className="text-zinc-600" />
        ) : (
          <ChevronRight size={12} className="text-zinc-600" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-3 animate-fade-in">
          {children}
        </div>
      )}
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
}) {
  const [draft, setDraft] = React.useState<string>(String(value));
  const [focused, setFocused] = React.useState(false);

  React.useEffect(() => {
    if (!focused) setDraft(String(value));
  }, [value, focused]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed === '' || trimmed === '-' || trimmed === '.') {
      setDraft(String(value));
      return;
    }
    let v = parseFloat(trimmed);
    if (isNaN(v)) {
      setDraft(String(value));
      return;
    }
    if (min !== undefined) v = Math.max(min, v);
    if (max !== undefined) v = Math.min(max, v);
    setDraft(String(v));
    if (v !== value) onChange(v);
  };

  return (
    <div>
      <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-1 block">
        {label}
      </label>
      <div className="relative">
        <input
          type="number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={(e) => {
            setFocused(true);
            e.target.select();
          }}
          onBlur={() => {
            setFocused(false);
            commit();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
              (e.target as HTMLInputElement).blur();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setDraft(String(value));
              (e.target as HTMLInputElement).blur();
            }
          }}
          step={step}
          className="w-full px-2 py-1.5 bg-[#0a0a0a] border border-white/10 rounded-lg text-xs text-zinc-200 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 pr-6"
        />
        {suffix && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-zinc-600 pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded-md transition-colors ${
        active
          ? 'bg-cyan-500/15 text-cyan-400'
          : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06]'
      }`}
    >
      {children}
    </button>
  );
}
